"use client";

import { useState } from "react";
import Link from "next/link";
import { generateKundali } from "@/lib/api/kundali";
import type { KundaliRequest, KundaliResponse } from "@/lib/api/kundali";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import MangalDoshaSeoContent from "./MangalDoshaSeoContent";
import PaidReportCTA from "@/components/PaidReportCTA";
import { trackCalculatorUsed, trackAgentRevenueOfferShown } from "@/lib/analytics";
import { useImpressionOnce } from "@/hooks/useImpressionOnce";
import { reportOfferCopyFor } from "@/lib/report-offer-copy";
import type { SEOReportPlanId } from "@/lib/seo-intents";
import RatingPrompt from "@/components/RatingPrompt";
import MangalDoshaDetails from "./MangalDoshaDetails";
import {
  findMangalDosha,
  isManglikResult,
  type MangalDosha,
  resultSummary,
  severityLabel,
} from "./mangalDoshaResult";

type ReportBirthDetails = {
  name: string;
  birth_date: string;
  birth_place: string;
  birth_time?: string | null;
};

export default function MangalDoshaClient({
  defaultLanguage = "en",
}: {
  defaultLanguage?: "en" | "hi";
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<KundaliResponse | null>(null);

  async function handleSubmit(data: KundaliRequest) {
    setLoading(true);
    setError(null);
    try {
      const response = await generateKundali({ ...data, skip_interpretation: true });
      setResult(response);
      trackCalculatorUsed({ calculator: "mangal-dosha-calculator" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  const mangalDosha = result ? findMangalDosha(result.doshas) : null;
  const isManglik = isManglikResult(mangalDosha);
  const mangalSummary = mangalDosha ? resultSummary(mangalDosha) : null;

  return (
    <div className="intent-interface-diagnostic-first sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <MangalDoshaHeader />

        {!result && (
          <MangalDoshaForm
            loading={loading}
            error={error}
            defaultLanguage={defaultLanguage}
            onSubmit={handleSubmit}
          />
        )}

        {result && (
          <MangalDoshaResultPanel
            result={result}
            dosha={mangalDosha}
            isManglik={isManglik}
            summary={mangalSummary}
            defaultLanguage={defaultLanguage}
            onReset={() => {
              setResult(null);
              setError(null);
            }}
          />
        )}

        <MangalDoshaSeoContent />

        <CalculatorCrossLinks currentPage="mangal-dosha-calculator" />
      </div>
    </div>
  );
}

function MangalDoshaHeader() {
  return (
    <div className="text-center mb-10">
      <p
        className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3"
        style={{ color: "var(--ish-accent)" }}
      >
        Mangal Dosha Pariksha
      </p>
      <h1
        className="text-[28px] sm:text-[36px] font-bold leading-tight"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        Are You Manglik? Free Mangal Dosh Check by Date of Birth
      </h1>
      <p
        className="mt-3 text-[15px] leading-relaxed max-w-md mx-auto"
        style={{ color: "var(--ish-text-muted)" }}
      >
        Enter your birth details and check if you are Manglik. Get instant severity analysis, dosha
        cancellation check, and Vedic remedies — 100% free.
      </p>
      <MangalTrustSignals />
    </div>
  );
}

function MangalTrustSignals() {
  return (
    <div
      className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] font-medium"
      style={{ color: "var(--ish-text-faint)" }}
    >
      <TrustSignal icon="shield" label="100% Accurate" />
      <TrustSignal icon="clock" label="Instant Results" />
      <TrustSignal icon="user" label="100% Free" />
    </div>
  );
}

function TrustSignal({ icon, label }: { icon: "shield" | "clock" | "user"; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <TrustSignalIcon icon={icon} />
      {label}
    </span>
  );
}

function TrustSignalIcon({ icon }: { icon: "shield" | "clock" | "user" }) {
  const icons = {
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    clock: (
      <>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </>
    ),
    user: (
      <>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </>
    ),
  };

  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      {icons[icon]}
    </svg>
  );
}

function MangalDoshaForm({
  loading,
  error,
  defaultLanguage,
  onSubmit,
}: {
  loading: boolean;
  error: string | null;
  defaultLanguage: "en" | "hi";
  onSubmit: (data: KundaliRequest) => Promise<void>;
}) {
  return (
    <div className="mx-auto max-w-md">
      <BirthDetailsForm
        onSubmit={onSubmit}
        loading={loading}
        submitLabel={defaultLanguage === "hi" ? "Mangal Dosh check karein" : "Check Mangal Dosha"}
        error={error ?? undefined}
        language={defaultLanguage}
        analyticsToolName="mangal-dosha-calculator"
      />
    </div>
  );
}

function MangalDoshaResultPanel({
  result,
  dosha,
  isManglik,
  summary,
  defaultLanguage,
  onReset,
}: {
  result: KundaliResponse;
  dosha: MangalDosha | null;
  isManglik: boolean;
  summary: string | null;
  defaultLanguage: "en" | "hi";
  onReset: () => void;
}) {
  return (
    <div className="space-y-6">
      {isManglik && dosha ? (
        <ManglikDetectedCard dosha={dosha} summary={summary} />
      ) : (
        <NotManglikCard summary={summary} />
      )}
      <MangalRatingPrompt />
      <MangalReportOffer result={result} isManglik={isManglik} defaultLanguage={defaultLanguage} />
      <MangalResultActions onReset={onReset} />
    </div>
  );
}

function ManglikDetectedCard({ dosha, summary }: { dosha: MangalDosha; summary: string | null }) {
  return (
    <div
      className="card-sacred p-6 sm:p-8"
      style={{
        borderColor: "rgba(220, 38, 38, 0.3)",
        backgroundColor: "rgba(254, 242, 242, 0.5)",
      }}
    >
      <div className="text-center mb-5">
        <span
          className="inline-flex items-center justify-center w-14 h-14 rounded-full text-2xl mb-3"
          style={{ backgroundColor: "rgba(220, 38, 38, 0.1)" }}
        >
          &#x2642;
        </span>
        <h2
          className="text-[24px] font-bold"
          style={{
            fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
            color: "var(--color-error-text)",
          }}
        >
          Mangal Dosha Detected
        </h2>
        {/* inert-by-design: severity percentage is a derived summary metric — the dosha itself is fully explained by the Explainable below */}
        <p className="text-[14px] mt-1" style={{ color: "var(--ish-text-muted)" }}>
          {severityLabel(dosha.severity)} · Severity {Math.round(dosha.severity * 100)}%
        </p>
        {summary && (
          <p className="mx-auto mt-3 max-w-sm text-[13px] leading-relaxed text-ish-muted">
            {summary}
          </p>
        )}
      </div>
      <div className="divider-warm my-5" />
      <MangalDoshaDetails dosha={dosha} />
    </div>
  );
}

function NotManglikCard({ summary }: { summary: string | null }) {
  return (
    <div
      className="card-sacred p-6 sm:p-8 text-center"
      style={{
        borderColor: "rgba(22, 163, 74, 0.3)",
        backgroundColor: "rgba(240, 253, 244, 0.5)",
      }}
    >
      <span
        className="inline-flex items-center justify-center w-14 h-14 rounded-full text-2xl mb-3"
        style={{ backgroundColor: "rgba(22, 163, 74, 0.1)" }}
      >
        &#x2713;
      </span>
      <h2
        className="text-[24px] font-bold"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--color-success-text-dark)",
        }}
      >
        Not Manglik
      </h2>
      <p className="text-[14px] mt-2 max-w-sm mx-auto" style={{ color: "var(--ish-text-muted)" }}>
        No Mangal Dosha was detected in your birth chart. Mars is placed in a favorable position
        from the checked reference points and does not create Manglik effects in this calculator.
      </p>
      {summary && (
        <p className="text-[13px] mt-3 max-w-sm mx-auto" style={{ color: "var(--ish-text-faint)" }}>
          {summary}
        </p>
      )}
    </div>
  );
}

function MangalRatingPrompt() {
  return (
    <div style={{ marginTop: 24 }}>
      <RatingPrompt
        source={"tool_result" as never}
        mode="thumbs+text"
        prompt="Was this Mangal Dosha check accurate?"
        context={{
          surface: "mangal_dosha_result",
          path: typeof window !== "undefined" ? window.location.pathname : null,
        }}
      />
    </div>
  );
}

function MangalReportOffer({
  result,
  isManglik,
  defaultLanguage,
}: {
  result: KundaliResponse;
  isManglik: boolean;
  defaultLanguage: "en" | "hi";
}) {
  const birth = birthDetailsForReport(result);
  const hi = defaultLanguage === "hi";
  const sourcePath = hi ? "/hi/mangal-dosha-calculator" : "/mangal-dosha-calculator";
  const planId: SEOReportPlanId = isManglik ? "dosha_remedy_plan" : "brihat_kundali";
  const placement = "mangal_dosha_result_primary";
  // Fire the offer-seen impression ONCE per presentation so this value surface's
  // reach is measurable in the funnel (rule 46 leading indicator: offer_seen →
  // offer_clicked). Hook runs before the early return (rules of hooks) with a
  // null key when birth data is missing.
  useImpressionOnce(birth ? `${planId}|${placement}|${sourcePath}` : null, () => {
    trackAgentRevenueOfferShown({
      offer_type: "report",
      plan_id: planId,
      source_path: sourcePath,
      intent: isManglik ? "dosha_remedy" : "chart_report",
      placement,
      checkout_channel: "ishvaram_report",
      commerce_system: "ishvaram",
      customer_problem: "mangal_dosh",
      urgency: isManglik ? "high" : "medium",
      route_rank_score: 90,
      route_rank_reason: "mangal_dosha_result",
      ranked_offer_order: [planId],
    });
  });
  if (!birth) return null;
  const planCopy = reportOfferCopyFor(planId, defaultLanguage);
  const headline = mangalReportHeadline(isManglik, defaultLanguage);
  const subhead = mangalReportSubhead(defaultLanguage);

  return (
    <PaidReportCTA
      planId={planId}
      priceInr={planCopy.priceInr}
      headline={headline}
      subhead={subhead}
      language={defaultLanguage}
      source={hi ? "hi-mangal-dosha-calculator" : "mangal-dosha-calculator"}
      analyticsContext={{
        placement,
        checkoutChannel: "ishvaram_report",
        commerceSystem: "ishvaram",
        intent: isManglik ? "dosha_remedy" : "chart_report",
        sourcePath,
        customerProblem: "mangal_dosh",
        urgency: isManglik ? "high" : "medium",
        confidence: 0.9,
        routeRankScore: 90,
        routeRankReason: "mangal_dosha_result",
        rankedOfferOrder: [planId],
      }}
      reportInputs={{
        name: birth.name,
        birth_date: birth.birth_date,
        birth_time: birth.birth_time || null,
        birth_place: birth.birth_place,
        language: defaultLanguage,
      }}
    />
  );
}

function mangalReportHeadline(isManglik: boolean, language: "en" | "hi"): string {
  if (language === "hi") {
    return isManglik
      ? "Mangal Dosha upay + full chart report paayein"
      : "Brihat Kundali Report - all 9 dosha checked";
  }
  return isManglik
    ? "Get personalised Mangal Dosha remedies + full chart"
    : "Get your full Brihat Kundali — all 9 doshas checked";
}

function mangalReportSubhead(language: "en" | "hi"): string {
  if (language === "hi") {
    return "25-page PDF - dosha analysis, personalised upay, dasha timing, marriage timing, Kuja/Mangal remedies aur gemstone guidance.";
  }
  return "25-page PDF — detailed dosha analysis, personalised upay, dasha predictions, marriage timing, remedies for Kuja/Mangal, gemstone guidance.";
}

function birthDetailsForReport(result: KundaliResponse): ReportBirthDetails | null {
  const birth = result.birth_details as ReportBirthDetails | undefined;
  if (!birth?.name || !birth?.birth_date || !birth?.birth_place) return null;
  return birth;
}

function MangalResultActions({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Link
        href="/free-kundali"
        className="btn-sacred text-center font-semibold py-3 px-6 rounded-lg text-[14px]"
      >
        Get Full Kundali Reading &rarr;
      </Link>
      <button
        onClick={onReset}
        className="text-center font-semibold py-3 px-6 rounded-lg text-[14px] transition-all duration-200"
        style={{
          color: "var(--ish-text-muted)",
          border: "1px solid var(--ish-border)",
        }}
      >
        Check Another Chart
      </button>
    </div>
  );
}
